import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../utils/firebase";
import { Helmet } from "react-helmet";

export default function BlogPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [otherBlogs, setOtherBlogs] = useState([]);

  // Fetch the main blog
  useEffect(() => {
    const fetchBlog = async () => {
      const ref = doc(db, "blogs", slug);
      const snap = await getDoc(ref);
      if (snap.exists()) setBlog({ id: snap.id, ...snap.data() });
    };
    fetchBlog();
  }, [slug]);

  // Fetch other blogs
  useEffect(() => {
    const fetchOtherBlogs = async () => {
      const querySnapshot = await getDocs(collection(db, "blogs"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const published = data.filter((b) => b.published);
      setOtherBlogs(published.sort((a, b) => new Date(b.date) - new Date(a.date)));
    };
    fetchOtherBlogs();
  }, []);

  if (!blog)
    return (
      <div className="p-10 text-center text-[#6cbb23] font-medium animate-pulse">
        Loading blog...
      </div>
    );

  const relatedBlogs = otherBlogs.filter((b) => b.id !== slug).slice(0, 5);

  return (
    <div className="max-w-7xl mx-auto py-16 px-6 bg-gray-50 min-h-screen">
      <Helmet>
        <title>{blog.title} — Zafflo</title>
        <meta name="description" content={blog.excerpt} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:image" content={blog.ogImage || blog.coverImage} />
      </Helmet>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Main Blog Content */}
        <div className="lg:col-span-2 bg-white shadow-md rounded-2xl overflow-hidden border border-gray-100">
          <img
            src={blog.coverImage}
            alt={blog.title}
            className="w-full h-80 object-cover"
          />

          <div className="p-8">
            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-extrabold mb-3 text-[#6cbb23] leading-tight">
              {blog.title}
            </h1>

            {/* Date and Author */}
            <p className="text-sm text-gray-500 mb-4">
              {new Date(blog.date).toDateString()} •{" "}
              <span className="font-medium text-[#6cbb23]">
                {blog.author || "Zafflo Team"}
              </span>
            </p>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-[#6cbb23]/10 text-[#6cbb23] text-xs font-medium px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Blog Content */}
            <div
              className="prose max-w-none prose-headings:text-gray-800 prose-a:text-[#6cbb23] prose-strong:text-gray-800 prose-p:text-gray-700"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </div>

        {/* Sidebar - Other Blogs */}
        <aside className="space-y-6">
          <h3 className="text-xl font-bold text-[#6cbb23] border-b pb-2">
            More Blogs
          </h3>

          {relatedBlogs.length > 0 ? (
            relatedBlogs.map((b) => (
              <Link
                key={b.id}
                to={`/blogs/${b.id}`}
                className="block bg-white rounded-xl shadow hover:shadow-lg transition p-4 border border-gray-100"
              >
                <div className="flex items-start space-x-4">
                  <img
                    src={b.coverImage}
                    alt={b.title}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 hover:text-[#6cbb23] line-clamp-2">
                      {b.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(b.date).toDateString()}
                    </p>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <p className="text-gray-500 text-sm">No more blogs available.</p>
          )}
        </aside>
      </div>

      {/* Back to blogs */}
      <div className="text-center mt-12">
        <Link
          to="/blogs"
          className="inline-block px-6 py-2.5 bg-[#6cbb23] text-white rounded-full font-semibold hover:bg-[#5aa51d] transition-colors duration-300"
        >
          ← Back to Blogs
        </Link>
      </div>
    </div>
  );
}
